// src/components/EstadoSuscripcionClinica.jsx
import React, { useEffect, useState } from 'react';
import { useClinic } from '../context/ClinicContext';
import axiosInstance from '../axiosInstance';
import {
  Box,
  Stack,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import { useNavigate } from 'react-router-dom';

const EstadoSuscripcionClinica = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { selectedClinicId, activeClinic } = useClinic();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let abort = false;
    (async () => {
      setLoading(true);
      try {
        if (!selectedClinicId) {
          setData(null);
          return;
        }
        const res = await axiosInstance.get('/clinica/suscripcion/estado/', {
          params: { clinic_id: selectedClinicId },
        });
        if (!abort) setData(res.data);
      } catch {
        if (!abort) setData(null);
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, [selectedClinicId]);

  // Contenedor visual coherente al sidebar
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 3,
        background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.06)} 0%, ${alpha(
          theme.palette.success.light,
          0.08
        )} 100%)`,
        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <SubscriptionsIcon sx={{ color: theme.palette.success.main }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Suscripción (Clínica)
        </Typography>
      </Stack>

      {!selectedClinicId ? (
        <Alert severity="info" icon={<WarningAmberIcon fontSize="inherit" />}>
          Selecciona una clínica para ver su suscripción.
        </Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={20} />
        </Box>
      ) : !data || data?.estado !== 'Activa' ? (
        <>
          <Chip
            icon={<WarningAmberIcon />}
            label="Sin suscripción activa"
            color="warning"
            size="small"
            sx={{ mb: 1, fontWeight: 700 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Clínica: {activeClinic?.clinica?.nombre || selectedClinicId}
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={() => navigate('/doctor/clinica/planes')}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            Ver Planes
          </Button>
        </>
      ) : (
        <>
          <Chip
            icon={<CheckCircleIcon />}
            label="Suscripción Activa"
            color="success"
            size="small"
            sx={{ mb: 1, fontWeight: 700 }}
          />
          <Stack spacing={0.5} sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {data?.plan?.nombre || 'Plan (—)'}
            </Typography>
            {data?.fecha_fin && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarMonthIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  Vence el: {data?.fecha_fin}
                </Typography>
              </Stack>
            )}
            {data?.plan?.precio && (
              <Stack direction="row" spacing={1} alignItems="center">
                <MonetizationOnIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  Precio: ${parseFloat(data.plan.precio).toFixed(2)}
                </Typography>
              </Stack>
            )}
          </Stack>

          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate('/doctor/clinica/suscripcion')}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            Gestionar Plan →
          </Button>
        </>
      )}
    </Box>
  );
};

export default EstadoSuscripcionClinica;
